import { useEffect, useState } from "react";
import { api, ROLE_LABEL } from "@/lib/api";
import {
  Gauge, TrendingUp, TrendingDown, Layers, GitBranch, Wallet, Award, Calendar as CalIcon,
  ChevronDown, ChevronRight,
} from "lucide-react";

const CY = "#89b4fa"; // cyan
const RD = "#ef5b6b"; // red
const GD = "#d4b876"; // gold
const MU = "#8C8C8C"; // muted

const currency = (n) => n == null ? "—" : "$" + Math.round(n).toLocaleString();
const signed = (n) => (n > 0 ? "+" : "") + n;

/** Colour a numeric variance against zero. */
const varianceColor = (n) => (n >= 0 ? CY : n >= -15 ? GD : RD);
/** Colour for a 0-100 health score. */
const scoreColor = (n) => (n == null ? MU : n >= 75 ? CY : n >= 50 ? GD : RD);

export default function CommandStrategic() {
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState(true);

  useEffect(() => {
    let alive = true;
    const load = () => api.get("/command/strategic").then(r => { if (alive) setData(r.data); }).catch(() => {});
    load();
    const t = setInterval(load, 30000);
    return () => { alive = false; clearInterval(t); };
  }, []);

  if (!data) {
    return (
      <div className="border-y border-white/10 py-6 mb-8 font-mono text-xs text-white/40">
        Compiling strategic index…
      </div>
    );
  }

  const ph = data.portfolio_health;
  const tp = data.term_pulse;
  const fr = data.fiscal_runway;
  const dd = data.dependency_debt;

  return (
    <section className="mb-10 border-y border-white/15 bg-gradient-to-b from-white/[0.02] to-transparent" data-testid="strategic-oversight">
      {/* Header bar */}
      <button
        onClick={() => setExpanded(v => !v)}
        className="w-full flex items-center justify-between px-6 py-4 hover:bg-white/[0.02] transition-colors"
        data-testid="strategic-toggle"
      >
        <div className="flex items-center gap-3">
          <span className="inline-block w-1.5 h-1.5 bg-[#d4b876] animate-pulse"/>
          <span className="label-mono text-[#d4b876]">Strategic oversight</span>
          <span className="font-mono text-[10px] text-white/40 hidden sm:inline">
            Council term {new Date(tp.term_start).getFullYear()}–{new Date(tp.term_end).getFullYear()} · refresh 30s
          </span>
        </div>
        {expanded ? <ChevronDown size={14} className="text-white/50"/> : <ChevronRight size={14} className="text-white/50"/>}
      </button>

      {expanded && (
        <div className="px-6 pb-6">
          {/* Row 1: The three big-number indices */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Portfolio Health Index */}
            <div className="panel p-5 corner-tick" data-testid="ph-index">
              <div className="flex items-baseline justify-between">
                <div className="label-mono flex items-center gap-2"><Gauge size={12}/> Portfolio health index</div>
                <span className="font-mono text-[10px] text-white/40">composite</span>
              </div>
              <div className="flex items-baseline gap-3 mt-3">
                <span className="font-display text-6xl tabular-nums" style={{color: scoreColor(ph.index)}}>
                  {ph.index ?? "—"}
                </span>
                <span className="font-mono text-xs text-white/40">/ 100</span>
              </div>
              <HealthMeter value={ph.index}/>
              <div className="font-mono text-[10px] text-white/50 mt-3 uppercase tracking-widest">
                {ph.open} open · {ph.completed} delivered
              </div>
            </div>

            {/* Term Mandate Pulse */}
            <div className="panel p-5 corner-tick" data-testid="term-pulse">
              <div className="flex items-baseline justify-between">
                <div className="label-mono flex items-center gap-2"><CalIcon size={12}/> Term mandate pulse</div>
                <span className="font-mono text-[10px] text-white/40">delivery vs. pace</span>
              </div>
              <div className="grid grid-cols-2 gap-4 mt-4">
                <div>
                  <div className="label-mono">Term elapsed</div>
                  <div className="font-display text-3xl mt-1">{tp.elapsed_pct}%</div>
                  <div className="h-1 bg-white/10 mt-2"><div className="h-full bg-white/60" style={{width: `${tp.elapsed_pct}%`}}/></div>
                </div>
                <div>
                  <div className="label-mono">Mandate delivered</div>
                  <div className="font-display text-3xl mt-1" style={{color: CY}}>{tp.delivery_pct}%</div>
                  <div className="h-1 bg-white/10 mt-2"><div className="h-full" style={{width: `${tp.delivery_pct}%`, background: CY}}/></div>
                </div>
              </div>
              <div className="mt-4 pt-3 border-t border-white/10 flex items-baseline justify-between">
                <span className="label-mono">Pace variance</span>
                <span className="font-display text-2xl tabular-nums flex items-center gap-1" style={{color: varianceColor(tp.variance_pct)}}>
                  {tp.variance_pct >= 0 ? <TrendingUp size={16}/> : <TrendingDown size={16}/>}
                  {signed(tp.variance_pct)}%
                </span>
              </div>
            </div>

            {/* Fiscal Runway */}
            <div className="panel p-5 corner-tick" data-testid="fiscal-runway">
              <div className="flex items-baseline justify-between">
                <div className="label-mono flex items-center gap-2"><Wallet size={12}/> Fiscal runway</div>
                <span className="font-mono text-[10px] text-white/40">forecast</span>
              </div>
              <div className="mt-4">
                <div className="flex items-baseline justify-between">
                  <span className="label-mono">Burn rate</span>
                  <span className="font-display text-3xl tabular-nums" style={{color: GD}}>{fr.burn_rate_pct}%</span>
                </div>
                <div className="h-1 bg-white/10 mt-2 relative overflow-hidden">
                  <div className="h-full" style={{width: `${Math.min(100, fr.burn_rate_pct)}%`, background: GD}}/>
                </div>
                <div className="font-mono text-[10px] text-white/50 mt-1 tabular-nums">
                  {currency(fr.spent)} of {currency(fr.allocated)}
                </div>
              </div>
              <div className="mt-4 pt-3 border-t border-white/10 grid grid-cols-2 gap-2">
                <div>
                  <div className="label-mono">Projected finish</div>
                  <div className="font-mono text-sm tabular-nums" style={{color: CY}}>{currency(fr.projected_finish_cost)}</div>
                </div>
                <div>
                  <div className="label-mono">Variance</div>
                  <div className="font-display text-lg tabular-nums" style={{color: varianceColor(-fr.variance_pct)}}>
                    {signed(fr.variance_pct)}%
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Row 2: Pillar allocation + Dependency debt + Velocity */}
          <div className="grid grid-cols-1 lg:grid-cols-[1.4fr_1fr_1fr] gap-4 mt-4">
            {/* Strategic Pillar Allocation */}
            <div className="panel p-5" data-testid="pillars">
              <div className="flex items-baseline justify-between mb-4">
                <div className="label-mono flex items-center gap-2"><Layers size={12}/> Strategic pillars</div>
                <span className="font-mono text-[10px] text-white/40">effort vs. capital</span>
              </div>
              <div className="space-y-3">
                {data.pillars.filter(p => p.effort_count > 0 || p.budget_allocated > 0).map(p => (
                  <div key={p.code}>
                    <div className="flex items-baseline justify-between font-mono text-xs">
                      <span className="text-white/80">{p.label}</span>
                      <span className="text-white/40 tabular-nums text-[10px]">
                        {p.effort_count} open · {currency(p.budget_allocated)}
                      </span>
                    </div>
                    <div className="flex gap-1 mt-1 h-1.5">
                      <div className="flex-1 bg-white/5 relative">
                        <div className="h-full absolute inset-y-0 left-0" style={{width: `${p.effort_pct}%`, background: CY, opacity: 0.85}}/>
                      </div>
                      <div className="flex-1 bg-white/5 relative">
                        <div className="h-full absolute inset-y-0 left-0" style={{width: `${p.budget_pct}%`, background: GD, opacity: 0.85}}/>
                      </div>
                    </div>
                    <div className="flex gap-1 font-mono text-[9px] text-white/40 mt-0.5">
                      <span className="flex-1">effort {p.effort_pct}%</span>
                      <span className="flex-1">capital {p.budget_pct}%</span>
                    </div>
                  </div>
                ))}
                {data.pillars.every(p => !p.effort_count && !p.budget_allocated) && (
                  <div className="font-mono text-xs text-white/40">No allocation data yet.</div>
                )}
              </div>
            </div>

            {/* Dependency Debt */}
            <div className="panel p-5" data-testid="dependency-debt">
              <div className="flex items-baseline justify-between mb-4">
                <div className="label-mono flex items-center gap-2"><GitBranch size={12}/> Dependency debt</div>
                <span className="font-mono text-[10px] text-white/40">cross-team</span>
              </div>
              <div className="grid grid-cols-2 gap-2 mb-4">
                <div>
                  <div className="label-mono">Cross-dept links</div>
                  <div className="font-display text-3xl mt-1">{dd.cross_dept_edges}</div>
                </div>
                <div>
                  <div className="label-mono">Longest chain</div>
                  <div className="font-display text-3xl mt-1" style={{color: dd.longest_chain > 3 ? GD : CY}}>{dd.longest_chain}</div>
                </div>
              </div>
              <div className="pt-3 border-t border-white/10">
                <div className="label-mono mb-2">Depts waiting on other depts</div>
                {dd.by_department.length === 0 ? (
                  <div className="font-mono text-xs text-white/40">None. Coordination clear.</div>
                ) : (
                  <div className="space-y-1">
                    {dd.by_department.slice(0, 5).map(row => (
                      <div key={row.code} className="flex justify-between font-mono text-xs">
                        <span className="text-white/80">{row.department}</span>
                        <span style={{color: RD}} className="tabular-nums">{row.waiting} blocked</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* 4-week velocity */}
            <div className="panel p-5" data-testid="velocity">
              <div className="flex items-baseline justify-between mb-4">
                <div className="label-mono flex items-center gap-2"><TrendingUp size={12}/> Velocity · 4w</div>
                <span className="font-mono text-[10px] text-white/40">dockets delivered</span>
              </div>
              <VelocityChart data={data.velocity_4wk}/>
              <div className="mt-4 pt-3 border-t border-white/10 grid grid-cols-3 gap-2 font-mono text-[10px]">
                <div>
                  <div className="text-white/40 uppercase tracking-widest">Sessions</div>
                  <div className="text-white text-lg font-display">{data.cadence.sessions_held_90d}</div>
                  <div className="text-white/40">held 90d</div>
                </div>
                <div>
                  <div className="text-white/40 uppercase tracking-widest">BCRs</div>
                  <div className="text-white text-lg font-display" style={{color: CY}}>{data.cadence.bcrs_delivered}</div>
                  <div className="text-white/40">delivered</div>
                </div>
                <div>
                  <div className="text-white/40 uppercase tracking-widest">Consults</div>
                  <div className="text-white text-lg font-display" style={{color: GD}}>{data.cadence.surveys_closed_90d + data.cadence.active_official_polls}</div>
                  <div className="text-white/40">active/closed</div>
                </div>
              </div>
            </div>
          </div>

          {/* Row 3: Leadership Accountability */}
          <div className="panel p-5 mt-4" data-testid="accountability">
            <div className="flex items-baseline justify-between mb-4">
              <div className="label-mono flex items-center gap-2"><Award size={12}/> Leadership accountability index</div>
              <span className="font-mono text-[10px] text-white/40">delivery-weighted · elected + executive</span>
            </div>
            {data.accountability.length === 0 ? (
              <div className="font-mono text-xs text-white/40">No leadership-owned dockets yet.</div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3">
                {data.accountability.map(a => (
                  <div key={a.owner_id} className="border border-white/10 p-3">
                    <div className="flex items-baseline justify-between">
                      <span className="font-display text-2xl tabular-nums" style={{color: scoreColor(a.score)}}>{a.score}</span>
                      <span className="font-mono text-[9px] text-white/40 uppercase tracking-widest">score</span>
                    </div>
                    <div className="font-mono text-xs text-white/80 mt-1 truncate">{a.name}</div>
                    <div className="font-mono text-[9px] text-white/40 uppercase tracking-widest">{ROLE_LABEL[a.role]}</div>
                    <div className="flex gap-2 mt-2 font-mono text-[10px]">
                      <span className="text-white/60">{a.assigned}</span>
                      <span className="text-white/30">|</span>
                      <span style={{color: CY}}>{a.delivered}✓</span>
                      {a.off_track > 0 && <span style={{color: RD}}>{a.off_track}!</span>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </section>
  );
}

function HealthMeter({ value }) {
  if (value == null) return null;
  return (
    <div className="mt-3 flex gap-0.5">
      {Array.from({ length: 20 }).map((_, i) => {
        const pos = (i + 1) * 5;
        const on = pos <= value;
        const color = pos <= 40 ? RD : pos <= 70 ? GD : CY;
        return <div key={i} className="h-2 flex-1" style={{background: on ? color : "rgba(255,255,255,0.08)"}}/>;
      })}
    </div>
  );
}

function VelocityChart({ data }) {
  const max = Math.max(1, ...data.map(w => w.delivered));
  return (
    <div className="flex items-end gap-2 h-24">
      {data.map((w, i) => {
        const h = (w.delivered / max) * 100;
        const isCurrent = i === data.length - 1;
        return (
          <div key={i} className="flex-1 flex flex-col items-center gap-1">
            <span className="font-mono text-[10px] tabular-nums" style={{color: isCurrent ? CY : "rgba(255,255,255,0.5)"}}>
              {w.delivered}
            </span>
            <div className="w-full bg-white/5 relative" style={{height: 60}}>
              <div
                className="absolute bottom-0 left-0 right-0 transition-all"
                style={{
                  height: `${h}%`,
                  background: isCurrent ? CY : "rgba(255,255,255,0.3)",
                }}
              />
            </div>
            <span className="font-mono text-[9px] text-white/40">
              {new Date(w.week_ending).toLocaleDateString(undefined, { month: "numeric", day: "numeric" })}
            </span>
          </div>
        );
      })}
    </div>
  );
}
